import { EpisodeState, Gender, StorySlug } from "@/api/episodes-types";
import { ImageState, ManualEditState } from "@/api/images-types";
import { MaskState } from "@/api/masks-types";
import { PromptType } from "@/api/prompts-types";
import { SubmissionState } from "@/api/submission-types";
import { ModelOption } from "@/components/story-generation/types";
import { SubmissionStateChip } from "@/components/ui/state-chips/SubmissionStateChip";
import { List, ListItem, Typography } from "@mui/material";
import {
  getGridDateOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridColDef,
  GridFilterOperator,
} from "@mui/x-data-grid";
import { pretty, prettyStoryType } from "./pretty";
import { rejectionReasons } from "./rejection-reasons";
import { storyCategories, storySubcategories } from "./story-categories";

export const containsOnlyOperator: GridFilterOperator[] =
  getGridStringOperators().filter((op) => op.value === "contains");

export const isOnlyOperator: GridFilterOperator[] =
  getGridSingleSelectOperators().filter((op) => op.value === "is");

export const dateIsOnlyOperator: GridFilterOperator[] =
  getGridDateOperators().filter((op) => op.value === "is");

export const storyTypeOptions = Object.values(StorySlug).map((storyType) => ({
  value: storyType,
  label: prettyStoryType(storyType),
}));

export const genderOptions = Object.values(Gender).map((gender) => ({
  value: gender,
  label: pretty(gender),
}));

export const promptTypeOptions = Object.values(PromptType).map((type) => ({
  value: type,
  label: pretty(type),
}));

export const submissionStateOptions = Object.values(SubmissionState).map(
  (state) => ({
    value: state,
    label: pretty(state, "capitalLetters"),
  }),
);

export const imageStateOptions = Object.values(ImageState).map((state) => ({
  value: state,
  label: pretty(state, "capitalLetters"),
}));

export const maskStateOptions = Object.values(MaskState).map((state) => ({
  value: state,
  label: pretty(state, "capitalLetters"),
}));

export const episodeStateOptions = Object.values(EpisodeState).map((state) => ({
  value: state,
  label: pretty(state, "capitalLetters"),
}));

export const manualEditStateOptions = Object.values(ManualEditState).map(
  (state) => ({
    value: state,
    label: pretty(state),
  }),
);

export const modelOptions = Object.values(ModelOption).map((modelName) => ({
  value: modelName,
  label: modelName,
}));

export const rejectionReasonsOptions = rejectionReasons.image.map((reason) => ({
  value: reason.title,
  label: reason.title,
}));

export const categoryOptions = storyCategories.map((category) => ({
  value: category,
  label: category,
}));

export const subcategoryOptions = (category: string) =>
  storySubcategories[category as keyof typeof storySubcategories]?.map(
    (subcategory) => ({
      value: subcategory,
      label: subcategory,
    }),
  ) || [];

type Timestamped = {
  createdAt: string;
  updatedAt: string;
};

export function timestampColumns<T extends Timestamped>(): GridColDef<T>[] {
  return [
    {
      field: "createdAt",
      headerName: "Created At",
      width: 180,
      type: "date",
      filterable: false,
      valueGetter: (_, row) => new Date(row.createdAt),
      valueFormatter: (_, row) => new Date(row.createdAt).toLocaleString(),
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 180,
      type: "date",
      filterable: false,
      valueGetter: (_, row) => new Date(row.updatedAt),
      valueFormatter: (_, row) => new Date(row.updatedAt).toLocaleString(),
    },
  ];
}

export function rejectionNotesColumn<T extends { rejectionNotes?: string }>(
  overrides?: Partial<GridColDef<T>>,
): GridColDef<T> {
  return {
    field: "rejectionNotes",
    headerName: "Rejection Notes",
    width: 210,
    renderCell: (params) => {
      return (
        <Typography
          variant="body2"
          sx={{
            height: "calc(100% - 20px)",
            overflowY: "auto",
            mx: 1.25,
            my: 1.25,
            ml: 0,
            mr: 0,
            p: 0,
            textWrap: "wrap",
            display: "block",
          }}
        >
          {params.row.rejectionNotes}
        </Typography>
      );
    },
    ...overrides,
  };
}

export function rejectionReasonsColumn<
  T extends { rejectionReasons: string[] },
>(overrides?: Partial<GridColDef<T>>): GridColDef<T> {
  return {
    field: "rejectionReasons",
    headerName: "Rejection Reasons",
    width: 300,
    renderCell: (params) => {
      return (
        <List
          sx={{
            height: "calc(100% - 20px)",
            overflowY: "auto",
            mx: 1.25,
            my: 1.25,
            ml: 0,
            mr: 0,
            p: 0,
            textWrap: "wrap",
          }}
        >
          {params.row.rejectionReasons.map((reason) => (
            <ListItem
              key={reason}
              sx={{ p: 0, pb: 1, "&:last-child": { pb: 0 } }}
            >
              <Typography variant="body2">{reason}</Typography>
            </ListItem>
          ))}
        </List>
      );
    },
    ...overrides,
  };
}

export function submissionStateColumn<
  T extends { submissionState: SubmissionState },
>(overrides?: Partial<GridColDef<T>>): GridColDef<T> {
  return {
    field: "submissionState",
    headerName: "Submitted",
    align: "center",
    headerAlign: "center",
    width: 180,
    renderCell: (params) => {
      return <SubmissionStateChip state={params.row.submissionState} />;
    },
    type: "singleSelect",
    valueOptions: submissionStateOptions,
    ...overrides,
  };
}
